import React from 'react'
import ReactDOM from 'react-dom'
import GraphicCircles from './GraphicCircles'
import GraphicCircles4k from './GraphicCircles4k'
import { useState } from 'react'
import useWindowDimensions from './useWindowDimensions'
import './styles.css'
import AppBar from '@material-ui/core/AppBar'
import Toolbar from '@material-ui/core/Toolbar'
import Box from '@material-ui/core/Box'
import Slider from '@material-ui/core/Slider'
import IconButton from '@material-ui/core/IconButton'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'
import Badge from '@material-ui/core/Badge'
import { makeStyles } from '@material-ui/core/styles'

import { SketchPicker } from 'react-color'

import {
	IconAdd,
	IconRemove,
	IconBackwards,
	IconCircle,
	IconDownload,
	IconEllipse,
	IconForwards,
	IconGrain,
	IconHighQuality,
	IconLeaf,
	IconLeaves,
	IconPalette,
	IconPatterning,
	IconPipette,
	IconRandom,
	IconSquare,
	IconStar,
	IconTriangle,
	IconWoven,
} from './Icons/'

import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles'

const LEAVES = { min: 2, default: 7, max: 22 }
const PATTERNINGS = { min: 1, default: 4, max: 22 }

const colorModes = {
	colorful: {
		main: ['#FFFFFF', '#C800FF', '#0015D6', '#FF005D', '#43C9CB', '#ffcc00'],
		bg: ['#7000CB', '#EDA1F8', '#C800FF'],
	},
	grayscale: {
		main: ['#FFFFFF', '#999999', '#CCCCCC', '#333333', '#1A1A1A', '#666666'],
		bg: ['#000000', '#141414', '#DDDDDD', '#FFFFFF'],
	},
	bw: {
		main: ['#FFFFFF', '#000000'],
		bg: ['#222222', '#141414', '#FFFFFF', '#000000'],
	},
	rbg: {
		main: ['#d13530', '#000000', '#3a8247'],
		bg: ['#21502B', '#9B2824', '#181818'],
	},
	rainbow: {
		main: ['#e5322b', '#eca347', '#f9f665', '#357a2a', '#001fe8', '#771a76'],
		bg: ['#28063D', '#5A217E', '#221381', '#C3507E', '#E03183', '#E9E864'],
	},
	// white: {
	// 	main: ['#FFFFFF', '#EEEEEE', '#FFFFFF', '#DDDDDD'],
	// 	bg: ['#333333', '#FFFFFF', '#141414'],
	// },
	yellow: {
		main: ['#FFDD00', '#FFBB00', '#FFFFFF'],
		bg: ['#FFAA00'],
	},
	green: {
		main: ['#64E3A5', '#00301C', '#64CC70', '#90E364', '#FFFFFF', '#C2DB46'],
		bg: ['#5FD9CE', '#DAF1AB', '#00443E'],
	},
	complementary: {
		main: ['#5E800B', '#9B23CC', '#DCFF85', '#9DCC23'],
		bg: ['#5B0080', '#9B23CC', '#5E800B'],
	},
	bright: {
		main: ['#59E3A8', '#70FA6E', '#BFE359', '#FFDD00'],
		bg: ['#81FDD2', '#49DFAB', '#36DCFA'],
	},
	monotone: {
		main: ['#B30089', '#7400B1', '#FF44B2', '#AB00BE', '#D612FF', '#FF005D'],
		bg: ['#760083', '#141414', '#310083', '#FFFFFF', '#F495FF'],
	},
	reds: {
		main: ['#400101', '#F2E2DC', '#730202', '#400101', '#F25C5C'],
		bg: ['#D9B4A7', '#400101'],
	},
	darkreds: {
		main: ['#D92B5A', '#BF1B57', '#F2B279', '#F29863'],
		bg: ['#260F20'],
	},
	romania: {
		main: ['#3D4073', '#F2E52E', '#F2B90F', '#F2BC57'],
		bg: ['#A66641', '#1E2153', '#FFF89F'],
	},
	travel: {
		main: ['#025159', '#03A696', '#F28705', '#F25D27', '#F20505'],
		bg: ['#007983', '#FF9700', '#862424'],
	},
	pastels: {
		main: ['#F2A0BE', '#BDAEF2', '#EBE9F2', '#85D3F2'],
		bg: ['#A79BF2'],
	},
	original: {
		main: ['#d3cfbc', '#FF613C', '#914b95', '#2d576e'],
		bg: ['#E74018'],
	},
}

const theme = createMuiTheme({
	palette: {
		type: 'dark',
		primary: {
			light: '#222',
			main: '#222',
			dark: '#222',
			contrastText: '#fefefe',
		},
		secondary: {
			light: '#777',
			main: '#777',
			dark: '#777',
			contrastText: '#fff',
		},
	},
})

const useStyles = makeStyles((theme) => ({
	margin: {
		margin: theme.spacing(1),
	},
	extendedIcon: {
		marginRight: theme.spacing(1),
	},
}))

function App() {
	const classes = useStyles()
	const { height, width } = useWindowDimensions()

	const initialMode = Object.keys(colorModes)[
		Math.floor(Math.random() * Object.keys(colorModes).length)
	]
	const [shapeSize] = useState(4)
	const [colors, setColors] = useState(colorModes[initialMode].main)
	const [colorMode, setColorMode] = useState(initialMode)
	const [colorPickerVisible, setColorPickerVisible] = useState(false)
	const [bg, setBg] = useState(colorModes[initialMode].bg[0])
	const [bgOptions, setBgOptions] = useState(colorModes[initialMode].bg)
	const [isExport, setExport] = useState(false)
	const [downloadCount, setDownloadCount] = useState(0)

	const [leaveCount, setLeaveCount] = useState(LEAVES.default)
	const [patterningCount, setPatterningCount] = useState(PATTERNINGS.default)
	const [direction, setDirection] = useState(1)
	const [grain, setGrain] = useState(false)
	const [shape, setShape] = useState('circle')
	const [isHiRes, setIsHiRes] = useState(false)
	const [randomValues, setRandomValues] = useState({
		start: 0,
		tip: 1,
		width: 2,
		form: 1,
	})

	const decreaseLeaves = () => {
		if (leaveCount > LEAVES.min) {
			setLeaveCount((v) => v - 1)
		} else setLeaveCount(LEAVES.min)
	}

	const increaseLeaves = () => {
		if (leaveCount < LEAVES.max) {
			setLeaveCount((v) => v + 1)
		} else setLeaveCount(LEAVES.max)
	}

	const handleLeavesSliderChange = (event, newValue) => {
		setLeaveCount(newValue)
	}

	const handlePatterningSliderChange = (event, newValue) => {
		setPatterningCount(newValue)
	}

	const decreasePatterning = () => {
		if (patterningCount > PATTERNINGS.min) {
			setPatterningCount((v) => v - 1)
		} else setPatterningCount(PATTERNINGS.min)
	}

	const increasePatterning = () => {
		if (patterningCount < PATTERNINGS.max) {
			setPatterningCount((v) => v + 1)
		} else setPatterningCount(PATTERNINGS.max)
	}

	const toggleColorMode = () => {
		const currentIndex = Object.keys(colorModes).indexOf(colorMode)
		if (currentIndex + 1 < Object.keys(colorModes).length) {
			setColors(colorModes[Object.keys(colorModes)[currentIndex + 1]].main)
			setBg(colorModes[Object.keys(colorModes)[currentIndex + 1]].bg[0])
			setBgOptions(colorModes[Object.keys(colorModes)[currentIndex + 1]].bg)
			setColorMode(Object.keys(colorModes)[currentIndex + 1])
		} else {
			setColors(colorModes[Object.keys(colorModes)[0]].main)
			setBg(colorModes[Object.keys(colorModes)[0]].bg[0])
			setBgOptions(colorModes[Object.keys(colorModes)[0]].bg)
			setColorMode(Object.keys(colorModes)[0])
		}
	}

	const toggleExport = () => {
		setExport(!isExport)
		setDownloadCount(downloadCount + 1)
	}

	const patterningScale = (v) => {
		if (v <= 16) return v
		if (v > 16) return 16 + 2 ** (v - 16)
		//if (v > 16) return 16 + (v - 16) ** 2
	}
	const leavesScale = (v) => {
		if (v <= 16) return v
		if (v > 16) return 16 + 2 ** (v - 16)
	}

	const handleRandomize = () => {
		setRandomValues({
			start: Math.random(),
			tip: 0.5 + Math.random() * 1.5,
			width: 1 + Math.random() * 2,
			form: -2 + Math.random() * 5,
		})
	}

	return (
		<ThemeProvider theme={theme}>
			<div>
				{isHiRes ? (
					<GraphicCircles4k
						width={width}
						height={height}
						innerCircles={patterningScale(patterningCount)}
						leaves={leavesScale(leaveCount)}
						shapeSize={shapeSize}
						shape={shape}
						colors={colors}
						backgroundColor={bg}
						direction={direction}
						grain={grain}
						randomizer={randomValues}
						export={() => {
							if (isExport) {
								setExport(false)
								return true
							} else return false
						}}
					/>
				) : (
					<GraphicCircles
						width={width}
						height={height}
						innerCircles={patterningScale(patterningCount)}
						leaves={leavesScale(leaveCount)}
						shapeSize={shapeSize}
						shape={shape}
						colors={colors}
						backgroundColor={bg}
						direction={direction}
						grain={grain}
						randomizer={randomValues}
						export={() => {
							if (isExport) {
								setExport(false)
								return true
							} else return false
						}}
					/>
				)}
			</div>
			<div
				style={{
					position: 'absolute',
					bottom: 10,
					width: '100%',
					display: 'flex',
					justifyContent: 'center',
				}}
			>
				<AppBar
					position="fixed"
					color="primary"
					style={{
						top: 'auto',
						bottom: 0,
						left: 'auto',
						right: 'auto',
					}}
				>
					<Box display="flex" flexWrap="wrap">
						<Toolbar style={{ flexGrow: 1 }}>
							<ToggleButtonGroup
								edge="start"
								value={shape}
								exclusive
								onChange={(event, newValue) => {
									if (newValue !== null) setShape(newValue)
								}}
								aria-label="text alignment"
							>
								<ToggleButton
									aria-label="Circle"
									value="circle"
									color="inherit"
									size="small"
								>
									<IconCircle />
								</ToggleButton>
								<ToggleButton
									aria-label="Square"
									value="square"
									color="inherit"
									size="small"
								>
									<IconSquare />
								</ToggleButton>
								<ToggleButton
									aria-label="Star"
									value="star"
									color="inherit"
									size="small"
								>
									<IconStar />
								</ToggleButton>
								<ToggleButton
									aria-label="Vertex"
									value="vertex"
									color="inherit"
									size="small"
								>
									<IconWoven />
								</ToggleButton>
								<ToggleButton
									aria-label="Triangle"
									value="triangle"
									color="inherit"
									size="small"
								>
									<IconTriangle />
								</ToggleButton>
								<ToggleButton
									aria-label="Ellipse"
									value="ellipse"
									color="inherit"
									size="small"
								>
									<IconEllipse />
								</ToggleButton>

								<ToggleButton
									aria-label="Flower"
									value="randomLeave"
									color="inherit"
									size="small"
								>
									<IconLeaf />
								</ToggleButton>
							</ToggleButtonGroup>

							<IconButton
								size="small"
								edge="end"
								aria-label="Randomize"
								onClick={handleRandomize}
								disabled={
									!(
										shape === 'randomLeave' ||
										shape === 'triangle' ||
										shape === 'ellipse'
									)
								}
								className={classes.margin}
								color="inherit"
								disableRipple
							>
								<IconRandom />
							</IconButton>
						</Toolbar>

						<Toolbar style={{ width: 280, flexGrow: 1 }}>
							<IconLeaves />
							<IconButton
								size="small"
								aria-label="Decrease Leaves"
								onClick={decreaseLeaves}
								className={classes.margin}
								color="inherit"
								disableRipple
							>
								<IconRemove />
							</IconButton>
							<Slider
								value={leaveCount}
								onChange={handleLeavesSliderChange}
								defaultValue={LEAVES.default}
								// getAriaValueText={valuetext}
								aria-label="Leaves"
								step={1}
								//scale={(x) => leavesScale(x)}
								min={LEAVES.min}
								max={LEAVES.max}
								valueLabelDisplay="auto"
								valueLabelFormat={(v) => leavesScale(v)}
								color="secondary"
							/>
							<IconButton
								size="small"
								aria-label="Increase Leaves"
								onClick={increaseLeaves}
								className={classes.margin}
								color="inherit"
								disableRipple
							>
								<IconAdd />
							</IconButton>
						</Toolbar>
						<Toolbar style={{ width: 280, flexGrow: 1 }}>
							<IconPatterning />
							<IconButton
								size="small"
								aria-label="Decrease Patterning"
								onClick={decreasePatterning}
								className={classes.margin}
								color="inherit"
								disableRipple
							>
								<IconRemove />
							</IconButton>
							<Slider
								value={patterningCount}
								onChange={handlePatterningSliderChange}
								defaultValue={PATTERNINGS.default}
								// getAriaValueText={valuetext}
								aria-label="Patternings"
								step={1}
								//scale={(x) => patterningScale(x)}
								min={PATTERNINGS.min}
								max={PATTERNINGS.max}
								valueLabelDisplay="auto"
								valueLabelFormat={(v) => patterningScale(v)}
								color="secondary"
							/>
							<IconButton
								size="small"
								aria-label="Increase Patterning"
								onClick={increasePatterning}
								className={classes.margin}
								color="inherit"
								disableRipple
							>
								<IconAdd />
							</IconButton>
						</Toolbar>
						<Toolbar style={{ flexGrow: 1 }}>
							<IconButton
								edge="start"
								size="small"
								aria-label="mode"
								onClick={toggleColorMode}
								className={classes.margin}
								color="inherit"
								disableRipple
							>
								<IconPalette />
							</IconButton>
							{colorPickerVisible && (
								<div style={{ position: 'absolute', bottom: '4em' }}>
									<SketchPicker
										disableAlpha
										color={bg}
										presetColors={bgOptions}
										onChange={(color) => {
											setBg(color.hex)
										}}
									/>
								</div>
							)}

							<ToggleButton
								aria-label="Picker"
								value="picker"
								selected={colorPickerVisible}
								onChange={() => setColorPickerVisible(!colorPickerVisible)}
								color="inherit"
								size="small"
							>
								<IconPipette />
							</ToggleButton>
							<div style={{ flexGrow: 1 }} />
							<ToggleButtonGroup
								value={direction}
								exclusive
								onChange={(event, newValue) => {
									if (newValue !== null) setDirection(newValue)
								}}
								aria-label="text alignment"
							>
								<ToggleButton
									aria-label="Leftwards"
									value={1}
									color="inherit"
									size="small"
								>
									<IconBackwards />
								</ToggleButton>
								<ToggleButton
									aria-label="Rightwards"
									value={-1}
									color="inherit"
									size="small"
								>
									<IconForwards />
								</ToggleButton>
							</ToggleButtonGroup>
							<div style={{ flexGrow: 1 }} />
							<ToggleButton
								aria-label="Grain"
								value="grain"
								selected={grain === true}
								onChange={() => setGrain((v) => !v)}
								color="inherit"
								size="small"
							>
								<IconGrain />
							</ToggleButton>
							<ToggleButton
								aria-label="4K"
								value="grain"
								selected={isHiRes === true}
								onChange={() => setIsHiRes((v) => !v)}
								color="inherit"
								size="small"
							>
								<IconHighQuality />
							</ToggleButton>
							<div style={{ flexGrow: 1 }} />
							<IconButton
								edge="end"
								size="small"
								aria-label="export"
								onClick={toggleExport}
								className={classes.margin}
								color="inherit"
								disableRipple
							>
								<Badge color="secondary" badgeContent={downloadCount}>
									<IconDownload />
								</Badge>
							</IconButton>
						</Toolbar>
					</Box>
				</AppBar>
			</div>
		</ThemeProvider>
	)
}

const rootElement = document.getElementById('root')
ReactDOM.render(<App />, rootElement)
